import { useState, useEffect, useRef, useCallback } from 'react';
import { Flex, Box, Input, Textarea, Spinner, Button, Switch } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuX, LuCheck, LuCircleAlert, LuUpload } from 'react-icons/lu';
import { CURRENCY_OPTIONS, LANGUAGE_OPTIONS } from '../../constants/currencies';
import { updateMenu, checkSlugAvailability, uploadMenuHeaderImage, deleteMenuHeaderImage, translateFields, type MenuResponse, type TranslatableField } from '../../api/menus';
import { toast } from 'react-toastify';
import ModalShell, { CancelButton, SaveButton, FieldLabel, LanguageTabs, inputStyles, selectStyle } from './ModalShell';
import ImagePickerModal from '../ImagePickerModal';

const SLUG_CHECK_DEBOUNCE_MS = 500;

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  menu: MenuResponse;
  onMenuUpdate: (menu: MenuResponse) => void;
  onDeleteMenu: () => void;
}

const SettingsModal = ({ open, onClose, menu, onMenuUpdate, onDeleteMenu }: SettingsModalProps) => {
  const { t } = useTranslation();
  const [names, setNames] = useState<TranslatableField>({});
  const [descriptions, setDescriptions] = useState<TranslatableField>({});
  const [menuSlug, setMenuSlug] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(['en']);
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [editLang, setEditLang] = useState('en');
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [normalizedSlug, setNormalizedSlug] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const slugTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const prevOpen = useRef(false);

  useEffect(() => {
    if (open && !prevOpen.current) {
      setNames({ ...menu.name });
      setDescriptions(menu.description ? { ...menu.description } : {});
      setMenuSlug(menu.slug);
      setCurrency(menu.currency);
      setAvailableLanguages(menu.available_languages || ['en']);
      setDefaultLanguage(menu.default_language || 'en');
      setEditLang(menu.default_language || 'en');
      setIsActive(menu.is_active);
      setSlugStatus('idle');
      setNormalizedSlug('');
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
    prevOpen.current = open;
    return () => {
      if (slugTimerRef.current) clearTimeout(slugTimerRef.current);
    };
  }, [open, menu]);

  const checkSlug = useCallback((slug: string) => {
    if (slugTimerRef.current) clearTimeout(slugTimerRef.current);

    if (!slug.trim() || slug === menu.slug) {
      setSlugStatus('idle');
      setNormalizedSlug('');
      return;
    }

    setSlugStatus('checking');
    slugTimerRef.current = setTimeout(async () => {
      try {
        const result = await checkSlugAvailability(slug, menu.id);
        setNormalizedSlug(result.slug);
        setSlugStatus(result.available ? 'available' : 'taken');
      } catch {
        setSlugStatus('idle');
      }
    }, SLUG_CHECK_DEBOUNCE_MS);
  }, [menu.id, menu.slug]);

  const handleHeaderImageReady = async (blob: Blob) => {
    setIsUploadingImage(true);
    try {
      const updated = await uploadMenuHeaderImage(menu.id, blob);
      onMenuUpdate(updated);
      toast.success(t('menus.headerImageUploaded'));
    } catch {
      toast.error(t('menus.headerImageFailed'));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveHeaderImage = async () => {
    setIsUploadingImage(true);
    try {
      const updated = await deleteMenuHeaderImage(menu.id);
      onMenuUpdate(updated);
      toast.success(t('menus.headerImageRemoved'));
    } catch {
      toast.error(t('menus.headerImageFailed'));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const defaultLangName = names[defaultLanguage] || Object.values(names)[0] || '';

  const handleAutoTranslate = async () => {
    const defaultName = names[defaultLanguage]?.trim();
    const defaultDesc = descriptions[defaultLanguage]?.trim();
    if (!defaultName && !defaultDesc) {
      toast.error(t('menus.fillDefaultLanguageFirst', 'Please fill in the default language fields first.'));
      return;
    }

    const fields: { [key: string]: string } = {};
    if (defaultName) fields.name = defaultName;
    if (defaultDesc) fields.description = defaultDesc;

    const targetLanguages = availableLanguages.filter(lang => {
      if (lang === defaultLanguage) return false;
      const nameEmpty = !names[lang]?.trim();
      const descEmpty = !descriptions[lang]?.trim();
      return nameEmpty || descEmpty;
    });

    if (targetLanguages.length === 0) {
      toast.info(t('menus.allFieldsFilled', 'All translation fields are already filled.'));
      return;
    }

    setIsTranslating(true);
    try {
      const result = await translateFields(menu.id, { fields, target_languages: targetLanguages });
      setNames(prev => {
        const updated = { ...prev };
        for (const lang of targetLanguages) {
          if (!updated[lang]?.trim() && result.translations[lang]?.name) {
            updated[lang] = result.translations[lang].name;
          }
        }
        return updated;
      });
      setDescriptions(prev => {
        const updated = { ...prev };
        for (const lang of targetLanguages) {
          if (!updated[lang]?.trim() && result.translations[lang]?.description) {
            updated[lang] = result.translations[lang].description;
          }
        }
        return updated;
      });
      toast.success(t('menus.translationComplete', 'Translations filled. Review and save.'));
    } catch (error) {
      toast.error(t('menus.translationFailed', 'Auto-translation failed.'));
      console.error(error);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const slugToSave = normalizedSlug || menuSlug;
      const cleanNames = Object.fromEntries(Object.entries(names).filter(([, v]) => v.trim()));
      const cleanDescs = Object.fromEntries(Object.entries(descriptions).filter(([, v]) => v.trim()));
      const updated = await updateMenu(menu.id, {
        name: cleanNames,
        slug: slugToSave,
        description: Object.keys(cleanDescs).length > 0 ? cleanDescs : undefined,
        currency,
        available_languages: availableLanguages,
        default_language: defaultLanguage,
        is_active: isActive,
      });
      onMenuUpdate(updated);
      const addedLanguages = availableLanguages.filter(l => !menu.available_languages.includes(l));
      if (addedLanguages.length > 0) {
        toast.success(t('menus.menuUpdatedTranslating'), { autoClose: 8000 });
      } else {
        toast.success(t('menus.menuUpdated'));
      }
      onClose();
    } catch (error) {
      toast.error(t('menus.updateFailed'));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={t('menus.menuSettings')}
      footer={
        <>
          <CancelButton onClick={onClose} label={t('common.cancel')} />
          <SaveButton onClick={handleSave} loading={isSaving} label={t('common.save')} disabled={slugStatus === 'taken' || slugStatus === 'checking'} />
        </>
      }
    >
      <Flex direction="column" rowGap="16px">
        {/* Header Image section */}
        <Box p="16px" borderRadius="8px" backgroundColor="neutral.700">
          <Flex direction="column" rowGap="8px">
            <FieldLabel>{t('menus.headerImage')}</FieldLabel>
            {menu.header_image_url ? (
              <Box position="relative" width="100%">
                <Box
                  width="100%"
                  borderRadius="8px"
                  borderWidth="1px"
                  borderColor="neutral.700"
                  overflow="hidden"
                  cursor="pointer"
                  onClick={() => setShowImagePicker(true)}
                  _hover={{ borderColor: 'neutral.600' }}
                >
                  <img src={menu.header_image_url} alt="" style={{ width: '100%', aspectRatio: '2 / 1', objectFit: 'cover', display: 'block' }} />
                </Box>
                <button
                  type="button"
                  onClick={handleRemoveHeaderImage}
                  disabled={isUploadingImage}
                  style={{
                    position: 'absolute', top: -6, right: -6,
                    width: 20, height: 20, padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: '#111111', border: '1px solid #383838',
                    borderRadius: '50%', cursor: 'pointer', zIndex: 2,
                  }}
                >
                  <LuX size={10} color="#9CA4B1" />
                </button>
              </Box>
            ) : (
              <Flex
                alignItems="center"
                gap="12px"
                py="12px"
                px="16px"
                borderRadius="8px"
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="neutral.600"
                backgroundColor="neutral.800"
                cursor="pointer"
                transition="all 0.2s"
                onClick={() => setShowImagePicker(true)}
                _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.700' }}
              >
                <Box p="8px" borderRadius="8px" backgroundColor="neutral.600" flexShrink={0}>
                  <LuUpload size={18} color="#CFD5DE" />
                </Box>
                <Flex direction="column" gap="2px">
                  <Box fontSize="14px" fontWeight="400" color="neutral.300">{t('menus.uploadImage')}</Box>
                  <Box fontSize="12px" fontWeight="400" color="neutral.500">PNG, JPG, WebP</Box>
                </Flex>
              </Flex>
            )}
            <ImagePickerModal
              open={showImagePicker}
              onClose={() => setShowImagePicker(false)}
              onImageReady={handleHeaderImageReady}
              aspectRatio={2}
            />
          </Flex>
        </Box>

        {/* General section */}
        <Box p="16px" borderRadius="8px" backgroundColor="neutral.700">
          <Flex direction="column" rowGap="16px">
            <LanguageTabs
              languages={availableLanguages}
              activeLang={editLang}
              onSelect={setEditLang}
              options={LANGUAGE_OPTIONS}
              onAutoTranslate={availableLanguages.length > 1 ? handleAutoTranslate : undefined}
              isTranslating={isTranslating}
            />

            <Flex direction="column" rowGap="8px">
              <FieldLabel>{t('menus.menuName')}</FieldLabel>
              <Input
                value={names[editLang] || ''}
                onChange={(e) => setNames(prev => ({ ...prev, [editLang]: e.target.value }))}
                placeholder={t('menus.enterMenuName')}
                {...inputStyles}
                backgroundColor="neutral.800"
              />
            </Flex>

            <Flex direction="column" rowGap="8px">
              <FieldLabel>{t('menus.description')}</FieldLabel>
              <Textarea
                value={descriptions[editLang] || ''}
                onChange={(e) => setDescriptions(prev => ({ ...prev, [editLang]: e.target.value }))}
                placeholder={t('menus.enterDescription')}
                {...inputStyles}
                backgroundColor="neutral.800"
                rows={3}
              />
            </Flex>

            <Flex direction="column" rowGap="8px">
              <FieldLabel>{t('menus.slug')}</FieldLabel>
              <Flex alignItems="center" gap="10px">
                <Input
                  value={menuSlug}
                  onChange={(e) => {
                    const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-');
                    setMenuSlug(val);
                    checkSlug(val);
                  }}
                  placeholder="my-restaurant-lunch-menu"
                  flex="1"
                  {...inputStyles}
                  backgroundColor="neutral.800"
                />
                <Box flexShrink={0} width="20px" display="flex" alignItems="center" justifyContent="center">
                  {slugStatus === 'checking' && <Spinner size="xs" color="neutral.500" />}
                  {slugStatus === 'available' && <LuCheck size={18} color="#4CAF50" />}
                  {slugStatus === 'taken' && <LuCircleAlert size={18} color="#EE4646" />}
                </Box>
              </Flex>
              {slugStatus === 'available' && (
                <Box fontSize="12px" color="#4CAF50" lineHeight="150%">
                  {normalizedSlug !== menuSlug ? `"${normalizedSlug}" is available` : 'Slug is available'}
                </Box>
              )}
              {slugStatus === 'taken' && (
                <Box fontSize="12px" color="error.600" lineHeight="150%">
                  {normalizedSlug ? `"${normalizedSlug}" is already taken` : 'Slug is already taken'}
                </Box>
              )}
              <Box fontSize="12px" color="neutral.500" lineHeight="150%">
                {t('menus.publicUrl')}: {window.location.origin}/m/{menuSlug || '...'}
              </Box>
            </Flex>
          </Flex>
        </Box>

        {/* Regional section */}
        <Box p="16px" borderRadius="8px" backgroundColor="neutral.700">
          <Flex direction="column" rowGap="16px">
            <Flex direction="column" rowGap="8px">
              <FieldLabel>{t('menus.currency')}</FieldLabel>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ ...selectStyle, backgroundColor: '#111111' }}>
                {CURRENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Flex>

            <Flex direction="column" rowGap="8px">
              <FieldLabel>{t('menus.languages')}</FieldLabel>
              <select
                value=""
                onChange={(e) => {
                  const val = e.target.value;
                  if (val && !availableLanguages.includes(val)) {
                    setAvailableLanguages(prev => [...prev, val]);
                  }
                  e.target.value = '';
                }}
                style={{ ...selectStyle, backgroundColor: '#111111' }}
              >
                <option value="" disabled>{t('menus.addLanguage', 'Add a language...')}</option>
                {LANGUAGE_OPTIONS
                  .filter(lang => !availableLanguages.includes(lang.value))
                  .map((lang) => (
                    <option key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
              </select>
              {availableLanguages.length > 0 && (
                <Flex gap="8px" flexWrap="wrap" mt="4px">
                  {availableLanguages.map((code) => {
                    const lang = LANGUAGE_OPTIONS.find(l => l.value === code);
                    const isDefault = code === defaultLanguage;
                    return (
                      <Flex
                        key={code}
                        alignItems="center"
                        gap="6px"
                        px="10px"
                        py="5px"
                        fontSize="13px"
                        fontWeight="400"
                        color="neutral.100"
                        backgroundColor="neutral.800"
                        borderWidth="1px"
                        borderColor="neutral.600"
                        borderRadius="6px"
                      >
                        {lang?.label || code}
                        {isDefault && (
                          <Box as="span" fontSize="11px" color="neutral.500">
                            ({t('common.default')})
                          </Box>
                        )}
                        {!isDefault && (
                          <Box
                            cursor="pointer"
                            color="neutral.500"
                            _hover={{ color: 'neutral.300' }}
                            onClick={() => setAvailableLanguages(prev => prev.filter(l => l !== code))}
                            ml="-2px"
                          >
                            <LuX size={12} />
                          </Box>
                        )}
                      </Flex>
                    );
                  })}
                </Flex>
              )}
            </Flex>

            {availableLanguages.length > 1 && (
              <Flex direction="column" rowGap="8px">
                <FieldLabel>{t('menus.defaultLanguage', 'Default Language')}</FieldLabel>
                <select value={defaultLanguage} onChange={(e) => setDefaultLanguage(e.target.value)} style={{ ...selectStyle, backgroundColor: '#111111' }}>
                  {availableLanguages.map((code) => {
                    const lang = LANGUAGE_OPTIONS.find(l => l.value === code);
                    return <option key={code} value={code}>{lang?.label || code}</option>;
                  })}
                </select>
              </Flex>
            )}
          </Flex>
        </Box>

        {/* Active toggle */}
        <Box p="16px" borderRadius="8px" backgroundColor="neutral.700">
          <Flex alignItems="center" justifyContent="space-between">
            <Flex direction="column" gap="2px">
              <Box fontSize="14px" fontWeight="500" color="neutral.300">
                {t('menus.active', 'Active')}
              </Box>
              <Box fontSize="12px" fontWeight="400" color="neutral.500">
                {t('menus.activeDescription', 'When inactive, this menu will not be publicly accessible')}
              </Box>
            </Flex>
            <Switch.Root checked={isActive} onCheckedChange={(e) => setIsActive(e.checked)}>
              <Switch.HiddenInput />
              <Switch.Control><Switch.Thumb /></Switch.Control>
            </Switch.Root>
          </Flex>
        </Box>

        {/* Danger Zone */}
        <Box
          backgroundColor="#1C0A0A"
          borderWidth="1px"
          borderColor="#3B1C1C"
          borderRadius="8px"
          p="24px"
          mt="8px"
        >
          <Flex direction="column" gap="16px">
            <Box fontSize="16px" fontWeight="500" color="error.600">
              {t('profile.dangerZone', 'Danger Zone')}
            </Box>
            <Box fontSize="14px" fontWeight="400" lineHeight="150%" color="neutral.400">
              {t('menus.deleteMenuWarning', 'Deleting this menu will permanently remove all categories, items, and images. This action cannot be undone.')}
            </Box>

            {!showDeleteConfirm ? (
              <Box>
                <Button
                  onClick={() => setShowDeleteConfirm(true)}
                  height="fit-content"
                  py="8px"
                  px="16px"
                  fontWeight="500"
                  fontSize="13px"
                  color="neutral.0"
                  backgroundColor="error.900"
                  borderRadius="6px"
                  _hover={{ backgroundColor: 'error.700' }}
                >
                  {t('menus.deleteMenu')}
                </Button>
              </Box>
            ) : (
              <Flex direction="column" gap="12px">
                <Box
                  fontSize="13px"
                  fontWeight="400"
                  color="neutral.300"
                  dangerouslySetInnerHTML={{ __html: t('menus.typeMenuNameToConfirm', { name: defaultLangName }) }}
                />
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={defaultLangName}
                  p="10px"
                  fontWeight="400"
                  fontSize="14px"
                  lineHeight="150%"
                  color="neutral.300"
                  borderWidth="1px"
                  borderColor="neutral.700"
                  backgroundColor="neutral.800"
                  borderRadius="6px"
                  _placeholder={{ color: 'neutral.500' }}
                  _focus={{ borderColor: 'neutral.600', outline: 'none' }}
                />
                <Flex gap="12px">
                  <Button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                    height="fit-content"
                    py="8px"
                    px="16px"
                    fontWeight="500"
                    fontSize="13px"
                    color="neutral.300"
                    backgroundColor="transparent"
                    borderWidth="1px"
                    borderColor="neutral.600"
                    borderRadius="6px"
                    _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.700' }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={onDeleteMenu}
                    disabled={deleteConfirmText !== defaultLangName}
                    height="fit-content"
                    py="8px"
                    px="16px"
                    fontWeight="500"
                    fontSize="13px"
                    color="neutral.0"
                    backgroundColor="error.900"
                    borderRadius="6px"
                    _hover={{ backgroundColor: 'error.700' }}
                    _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                  >
                    {t('menus.confirmDelete', 'Confirm Deletion')}
                  </Button>
                </Flex>
              </Flex>
            )}
          </Flex>
        </Box>
      </Flex>
    </ModalShell>
  );
};

export default SettingsModal;
